// Utility functions for workspace data snapshots and validation

import { FailureRecord } from '@/types/failure';
import { Workspace } from '@/types/workspace';

/**
 * Create a data snapshot from current failure records
 */
export function createSnapshot(failures: FailureRecord[]): Workspace['dataSnapshot'] {
  return {
    failures: [...failures], // Deep copy to prevent mutations
    capturedAt: new Date().toISOString(),
    totalRecords: failures.length,
    checksum: calculateChecksum(failures),
  };
}

/**
 * Validate a snapshot against current data
 */
export function validateSnapshot(
  snapshot: Workspace['dataSnapshot'],
  currentData?: FailureRecord[]
): { isValid: boolean; warnings: string[] } {
  const warnings: string[] = [];

  if (!snapshot) {
    return { isValid: false, warnings: ['No snapshot data available'] };
  }

  // Check data integrity
  const currentChecksum = calculateChecksum(snapshot.failures);
  if (snapshot.checksum && currentChecksum !== snapshot.checksum) {
    warnings.push('Snapshot data integrity check failed');
  }

  // Check if current data differs (if provided)
  if (currentData) {
    if (currentData.length !== snapshot.totalRecords) {
      warnings.push(`Data count changed: ${snapshot.totalRecords} → ${currentData.length}`);
    }

    // Check if any failure IDs are different
    const snapshotIds = new Set(snapshot.failures.map((f) => f.failure_id));
    const currentIds = new Set(currentData.map((f) => f.failure_id));

    const addedCount = currentData.filter((f) => !snapshotIds.has(f.failure_id)).length;
    const removedCount = snapshot.failures.filter((f) => !currentIds.has(f.failure_id)).length;

    if (addedCount > 0) {
      warnings.push(`${addedCount} new failure record(s) added since snapshot`);
    }
    if (removedCount > 0) {
      warnings.push(`${removedCount} failure record(s) removed since snapshot`);
    }
  }

  // Check snapshot age
  const snapshotAge = Date.now() - new Date(snapshot.capturedAt).getTime();
  const daysSinceSnapshot = Math.floor(snapshotAge / (1000 * 60 * 60 * 24));

  if (daysSinceSnapshot > 30) {
    warnings.push(`Snapshot is ${daysSinceSnapshot} days old`);
  }

  return {
    isValid: warnings.length === 0,
    warnings,
  };
}

/**
 * Calculate a simple checksum for data integrity
 */
export function calculateChecksum(failures: FailureRecord[]): string {
  // Simple checksum based on failure IDs and operation times
  const data = failures
    .map((f) => `${f.failure_id}:${f.operation_time_years}`)
    .sort()
    .join('|');

  // Simple hash function (for localStorage use only)
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return hash.toString(16);
}

/**
 * Restore failure records from snapshot
 */
export function restoreFromSnapshot(snapshot: Workspace['dataSnapshot']): FailureRecord[] {
  if (!snapshot) {
    throw new Error('No snapshot data available');
  }

  // Validate snapshot integrity
  const validation = validateSnapshot(snapshot);
  if (!validation.isValid) {
    console.warn('Snapshot validation warnings:', validation.warnings);
  }

  return [...snapshot.failures]; // Return copy to prevent mutations
}

/**
 * Compare two sets of failure data to detect changes
 */
export function compareFailureData(
  data1: FailureRecord[],
  data2: FailureRecord[]
): {
  isIdentical: boolean;
  added: FailureRecord[];
  removed: FailureRecord[];
  modified: Array<{ original: FailureRecord; updated: FailureRecord }>;
} {
  const data1Map = new Map(data1.map((f) => [f.failure_id, f]));
  const data2Map = new Map(data2.map((f) => [f.failure_id, f]));

  const added: FailureRecord[] = [];
  const removed: FailureRecord[] = [];
  const modified: Array<{ original: FailureRecord; updated: FailureRecord }> = [];

  // Find added records
  for (const record of data2) {
    if (!data1Map.has(record.failure_id)) {
      added.push(record);
    }
  }

  // Find removed and modified records
  for (const record of data1) {
    const updated = data2Map.get(record.failure_id);
    if (!updated) {
      removed.push(record);
    } else {
      // Check if data has changed (simple comparison)
      if (JSON.stringify(record) !== JSON.stringify(updated)) {
        modified.push({ original: record, updated });
      }
    }
  }

  return {
    isIdentical: added.length === 0 && removed.length === 0 && modified.length === 0,
    added,
    removed,
    modified,
  };
}
